import * as gsap from "gsap";
import {Callback} from "./Reflection";

export interface ITween {
    target: any;
    pause(): void;
    resume(): void;
    kill(): void;
    isActive(): boolean;
    paused(): boolean;
    progress(value?: number, suppressEvents?: boolean): boolean | ITween | any;
    time(): number;
    totalTime(): number;
}

export type TweenParams<T> = { [P in keyof T]?: T[P] } & {
    ease?: gsap.Ease;
    delay?: number;
    repeatDelay?: number;
    immediateRender?: boolean;
    repeat?: number;
    yoyo?: boolean;
    onStart?: () => void;
    onUpdate?: () => void;
    onComplete?: () => void;
    onRepeat?: () => void;
    data?: any;
    bezier?: { type: "thru" | "soft" | "quadratic" | "cubic", values: Array<{ x: number, y: number }>, curviness?: number };
};

export const Ease = {

    backIn: gsap.Back.easeIn,
    backOut: gsap.Back.easeOut,
    backInOut: gsap.Back.easeInOut,

    bounceIn: gsap.Bounce.easeIn,
    bounceOut: gsap.Bounce.easeOut,
    bounceInOut: gsap.Bounce.easeInOut,

    circIn: gsap.Circ.easeIn,
    circOut: gsap.Circ.easeOut,
    circInOut: gsap.Circ.easeInOut,

    elasticIn: gsap.Elastic.easeIn,
    elasticOut: gsap.Elastic.easeOut,
    elasticInOut: gsap.Elastic.easeInOut,

    expoIn: gsap.Expo.easeIn,
    expoOut: gsap.Expo.easeOut,
    expoInOut: gsap.Expo.easeInOut,

    none: gsap.Linear.easeNone,

    quadIn: gsap.Power1.easeIn,
    quadOut: gsap.Power1.easeOut,
    quadInOut: gsap.Power1.easeInOut,

    cubicIn: gsap.Power2.easeIn,
    cubicOut: gsap.Power2.easeOut,
    cubicInOut: gsap.Power2.easeInOut,

    quartIn: gsap.Power3.easeIn,
    quartOut: gsap.Power3.easeOut,
    quartInOut: gsap.Power3.easeInOut,

    quintIn: gsap.Power4.easeIn,
    quintOut: gsap.Power4.easeOut,
    quintInOut: gsap.Power4.easeInOut,

    sineIn: gsap.Sine.easeIn,
    sineOut: gsap.Sine.easeOut,
    sineInOut: gsap.Sine.easeInOut,

    slowMo: gsap.SlowMo.ease,
    rough: gsap.RoughEase.ease,

    custom(func: (p: number) => number) {
        const ease = new gsap.Ease();
        ease.getRatio = func;
        return ease;
    },
};

export const Animatable = {
    tweenTo<T>(this: T, duration: number, ease: gsap.Ease, params: TweenParams<T>) {
        params.ease = ease;
        return gsap.TweenMax.to(this, duration, params);
    },
    tweenFrom<T>(this: T, duration: number, ease: gsap.Ease, params: TweenParams<T>) {
        params.ease = ease;
        return gsap.TweenMax.from(this, duration, params);
    },
    alphaTo<T extends PIXI.Container>(this: T, alpha: number, duration: number, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.to(this, duration, {
            ease: gsap.Linear.ease,
            alpha,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    alphaFrom<T extends PIXI.Container>(this: T, alpha: number, duration: number, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.from(this, duration, {
            ease: gsap.Linear.ease,
            alpha,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    scaleTo<T extends PIXI.Container & { scaleXY: number }>(this: T, scale: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.to(this, duration, {
            ease,
            scaleXY: scale,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    scaleFrom<T extends PIXI.Container & { scaleXY: number }>(this: T, scale: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.from(this, duration, {
            ease,
            scaleXY: scale,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    moveXYTo<T extends PIXI.Container>(this: T, x: number, y: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.to(this, duration, {
            ease,
            x,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    moveXYFrom<T extends PIXI.Container>(this: T, x: number, y: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.from(this, duration, {
            ease,
            x,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    moveXTo<T extends PIXI.Container>(this: T, x: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.to(this, duration, {
            ease,
            x,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    moveXFrom<T extends PIXI.Container>(this: T, x: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.from(this, duration, {
            ease,
            x,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    moveYTo<T extends PIXI.Container>(this: T, y: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.to(this, duration, {
            ease,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    moveYFrom<T extends PIXI.Container>(this: T, y: number, duration: number, ease: gsap.Ease, params?: TweenParams<T> | Callback) {
        return gsap.TweenMax.from(this, duration, {
            ease,
            y,
            ...(typeof params === "function" ? {onComplete: params} : params || {}),
        });
    },
    delayedCall(this: any, time: number, cb: Callback) {
        if (time < 0 || isNaN(time)) {
            time = 0;
        }
        let i = 0;
        while (!isNaN(this["-tween-delay-" + i])) {
            i++;
        }
        this["-tween-delay-" + i] = 0;
        return gsap.TweenMax.to(this, time, {
            ease: Ease.none,
            ["-tween-delay-" + i]: time,
            onComplete: () => {
                delete this["-tween-delay-" + i];
                if (cb) {
                    cb();
                }
            },
        });
    },
    killTweens<T>(this: T) {
        gsap.TweenMax.killTweensOf(this);
    },
};
